import type { NewPaymentMethodFn } from '@/features/billing'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { BillingProfile } from '@/features/billing/profile/BillingProfile'
import { BillingMethodListItemViewModel } from '@/features/billing/payment-methods/BillingMethodListItemViewModel'
import { DeleteBillingMethodDialogViewModel } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogViewModel'
import { type Team } from '@/features/iam/teams/Team'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { computed } from 'mobx'
import { task } from 'mobx-task'

/**
 * View model for a team's payment methods.
 */
export class TeamPaymentsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Confirms deleting a payment method.
   */
  readonly deleteDialogVm

  /**
   * The payment methods.
   */
  readonly methods

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    public readonly team: Team,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _newPaymentMethod: NewPaymentMethodFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.deleteDialogVm = new DeleteBillingMethodDialogViewModel(
      this._billingProfileStore,
      this._createAlertErrorHandler,
    )
    this.methods = new ViewModelList({
      models: () => this.paymentMethods,
      create: (method) =>
        new BillingMethodListItemViewModel(
          method,
          this._billingProfileStore,
          this.deleteDialogVm,
          this._createAlertErrorHandler,
          { team: this.team },
        ),
    })
    this._lifecycle.manage(this.methods)
  }

  /**
   * Whether the list is busy with an action.
   */
  @computed
  get disabled() {
    return (
      this.fetch.pending ||
      this.addPaymentMethod.pending ||
      this.methods.items.some((methodVm) => methodVm.setAsDefault.pending)
    )
  }

  /**
   * The billing profile, once loaded.
   */
  @computed
  get billingProfile() {
    return (
      this._billingProfileStore.teamBillingProfile(this.team) ??
      BillingProfile.none
    )
  }

  /**
   * The payment methods of the billing profile.
   */
  @computed
  get paymentMethods() {
    return this.billingProfile.billingMethods.slice()
  }

  /**
   * Whether the current user may manage the team's payment methods.
   */
  @computed
  get canManageTeamBilling() {
    return this._billingProfileStore.canManage(this.billingProfile)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this.deleteDialogVm.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the team's billing profile.
   */
  fetch = task(async (signal: AbortSignal) => {
    const withRetry = this._createAlertRetryWrapper(
      'Loading payment methods failed',
      signal,
    )
    await withRetry(() =>
      this._billingProfileStore.fetchBillingProfileForTeam(this.team.id),
    )
  })

  /**
   * Add a team payment method.
   */
  addPaymentMethod = task.resolved(async () => {
    const method = await this._newPaymentMethod(
      this._getCurrentMember(this.team.workspace.id),
      this.team,
      this._lifecycle.signal,
    )
    if (method) {
      await this.methods.get(method)?.setAsDefault()
    }
  })
}
